import React, { useEffect, useState, CSSProperties, ReactNode } from 'react';
import { useRouter } from 'next/router';
import ReactHintFactory from 'react-hint';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { grid, tooltip } from 'styles';
import { attributesConditionsMatch } from 'shared-helpers/customAttributes';
import { getFormattedDate } from 'lib/common';
import { shouldShowUserConsentForm, shouldShowUserNameForm } from 'lib/users';
import { useThemeEditorContext } from './ThemeEditorContext';
import { SiteSettingsThemesEditor } from 'components/admin/settings/themes';
import { UserNameForm } from 'components/common';
import { UserCustomAttributesForm } from 'components/common/customAttributes';
import { UserConsentForm, UserConsentUpdate } from 'components/consent';
import { NoticeContainer } from 'components/common/notifications';
import { SessionTimeout } from 'components/login';
import { GuestInfoModal } from 'components/user';
import { EmbeddedScripts, NoscriptWarning } from '.';

import 'styles/fontAwesomeIcons';

const ReactHint = ReactHintFactory(React);

interface Props {
  appError?: boolean;
  appStyle?: CSSProperties;
  backgroundColor?: string;
  children: ReactNode;
}

const App = ({ appError, appStyle, backgroundColor, children }: Props) => {
  const router = useRouter();

  const { dispatch } = useDispatch();

  const { messages, serverConfig, user } = useReduxState();

  const { themeEditorWidth } = useThemeEditorContext();

  const [guestInfoModalVisible, setGuestInfoModalVisible] = useState(false);
  const [showButtonOutline, setShowButtonOutline] = useState(false);

  useEffect(() => {
    if (!appError) {
      showLoginNotification();
      getAccountDeleteRequests();
      getMyTeamUnreviewedItems();
    }

    // listen to route change event so we can track where user page was
    // initially accessed from and have the back link point back to that page
    const handleRouteChange = (url: string) => {
      if (sessionStorage) {
        const matchPageWithBackButton = /^(\/(admin\/)?[^\/]+\/)/;

        // see if next page matches a page with potentially a back button
        const match = url.match(matchPageWithBackButton);
        if (!match) {
          // otherwise remove previous path from session storage
          sessionStorage.removeItem('vuoPreviousPath');
          return;
        }

        const subRoot = match[0];

        const currentUrl = `${window.location.pathname}${window.location.search}`;

        const vuoPreviousPath = sessionStorage.getItem('vuoPreviousPath');

        // if there is no previous page in session storage or there is but
        // it does not match current subroot, we update the value
        if (!vuoPreviousPath || !vuoPreviousPath.startsWith(match[0])) {
          sessionStorage.setItem('vuoPreviousPath', `${subRoot}:${currentUrl}`);
        }
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, []);

  const appMouseDown = () => {
    setShowButtonOutline(false);
  };

  const appKeyDown = () => {
    setShowButtonOutline(true);
  };

  const getAccountDeleteRequests = async () => {
    if (
      !user?.roles.includes('superadmin') &&
      (!serverConfig.allowAdminToManageDeleteRequests ||
        !user?.permissions.includes('manage-users'))
    ) {
      return;
    }

    try {
      const response = await api.get('/admin/account-delete-requests');
      dispatch.setAccountDeleteRequests(response.data.accountDeleteRequests);
    } catch (err) {
      // probably no need to notify the user in this case
      console.error('failed to fetch account delete requests');
    }
  };

  const getMyTeamUnreviewedItems = async () => {
    if (!user?.roles?.includes('supervisor')) return;

    try {
      const response = await api.get(
        `/admin/supervisors/${user.id}/unreviewed-items`
      );
      dispatch.setSupervisorUnreviewedItems(response.data.unreviewedItems);
    } catch (err) {
      console.error('failed to fetch unreviewed team items for the supervisor');
    }
  };

  const showLoginNotification = () => {
    // check if we should show notification about last login
    const loginNotificationValue = document.cookie.replace(
      /(?:(?:^|.*;\s*)showLoginNotification\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    );
    if (
      serverConfig.loginShowPreviousLoginNotification &&
      loginNotificationValue
    ) {
      // delete cookie
      document.cookie =
        'showLoginNotification=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      // show notification
      if (user) {
        const decodedValue = decodeURIComponent(loginNotificationValue);
        let text = '';
        const name =
          serverConfig.userUseName && user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.username;
        if (decodedValue === 'no_previous_login') {
          text = `
            <div>${messages.authWelcome} ${name}${'!'}</div>
            <div>${messages.authWelcomeFirstLogin}</div>
          `;
        } else {
          text = `
            <div>${messages.authWelcome} ${name}${'!'}</div>
            <div>${messages.authWelcomePreviousLogin}</div>
            <div>${getFormattedDate({
              date: decodedValue,
              dateFormat: 'dd.MM.y HH:mm'
            })}.</div>
          `;
        }
        dispatch.addNotice({ text });
      }
    }
  };

  const requireTwoFactor =
    user &&
    user.authenticatorType === 'local' &&
    (user.twoFactorAuthIsEnabled ||
      (((user.roles.includes('admin') &&
        serverConfig?.authRequireTwoFactor === 'admins') ||
        (user.roles.includes('user') &&
          serverConfig?.authRequireTwoFactor === 'all')) &&
        attributesConditionsMatch({
          conditions: serverConfig?.authRequireTwoFactorConditions?.conditions,
          customAttributeValues: user.customAttributes,
          user
        })));

  return (
    <div
      className={`app-wrapper ${
        showButtonOutline ? '' : 'hide-button-outline'
      } ${
        serverConfig?.customThemeEnableEditor &&
        user?.roles?.includes('vuoadmin')
          ? 'with-theme-editor'
          : ''
      }`}
      onKeyDown={appKeyDown}
      onMouseDown={appMouseDown}
      style={appStyle}
    >
      <NoscriptWarning />
      <div className="select-menu-root" />
      <div
        className="datepicker-root"
        id="datepicker-root"
        style={{
          position: 'absolute',
          zIndex: 240
        }}
      />
      <div className="selection-menu-root" />
      <div className="dropdown-root" />

      {children}

      <div className="modal-root" />
      <div className="dynamic-tooltip-root" />
      <div className="reader-emoji-root" />
      <ReactHint
        attribute="data-tooltip"
        autoPosition={true}
        className="vuo-tooltip"
        events={true}
        delay={50}
      />

      {!appError ? (
        <>
          <EmbeddedScripts />
          <NoticeContainer />
          <SessionTimeout />
          {user?.roles.includes('guest') && (
            <GuestInfoModal
              modalVisible={guestInfoModalVisible}
              setModalVisible={setGuestInfoModalVisible}
            />
          )}
          {!requireTwoFactor ? (
            <>
              <UserCustomAttributesForm
                guestInfoModalVisible={guestInfoModalVisible}
              />
              {serverConfig?.userUseName && shouldShowUserNameForm(user) ? (
                <UserNameForm />
              ) : null}
            </>
          ) : null}
          {shouldShowUserConsentForm(user, serverConfig) ? (
            <UserConsentForm />
          ) : null}
          <UserConsentUpdate />

          {serverConfig?.customThemeEnableEditor &&
          user?.roles?.includes('vuoadmin') ? (
            <SiteSettingsThemesEditor />
          ) : null}
        </>
      ) : null}

      <style jsx global>
        {tooltip}
      </style>
      <style jsx global>
        {grid}
      </style>
      <style jsx global>{`
        /* base */
        html {
          font-size: 62.5%;
          height: 100%;
        }
        body {
          background-color: ${backgroundColor};
          font: 1.8em 'Source Sans Pro', sans-serif;
          margin: 0;
          height: 100%;
          min-width: 320px;
          overflow-x: hidden;
          padding: 0;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        #__next {
          height: 100%;
        }
        .app-wrapper {
          display: flex;
          flex-direction: column;
          min-height: 100%;
          width: 100%;
        }
        .app-wrapper.with-theme-editor {
          padding-left: ${themeEditorWidth}px;
        }
        *,
        *::after,
        *::before {
          box-sizing: border-box;
        }
        .container {
          margin: 0 auto;
          max-width: 1320px;
          padding: 0 15px;
          width: 100%;
        }
        .hide-button-outline :global(button:focus) {
          outline: 0;
        }
        .hide-button-outline :global(button.focus-background:focus) {
          background-color: transparent !important;
        }
        .hide-button-outline :global(.focus-box-shadow:focus),
        .hide-button-outline
          :global(.focus-box-shadow:focus .focus-box-shadow-child) {
          box-shadow: none !important;
        }
        .hide-button-outline :global(.focus-box-shadow-double:focus) {
          box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1) !important;
        }
        .hide-button-outline :global(.checkbox > input:focus + label .icon),
        .hide-button-outline
          :global(.checkbox > input:focus:checked + label .icon),
        .hide-button-outline :global(.radio > input:focus + label),
        .hide-button-outline :global(.radio > input:focus + .label label),
        .hide-button-outline
          :global(.radio > input:focus + .label label::before),
        .hide-button-outline
          :global(.radio > input:focus:checked + .label label::before),
        .hide-button-outline :global(.radio-button > input:focus + label),
        .hide-button-outline
          :global(.radio-button > input:focus + .label label),
        .hide-button-outline
          :global(.radio-button > input:focus + .label label::before),
        .hide-button-outline
          :global(.radio-button > input:focus:checked + .label label::before),
        .hide-button-outline :global(.switch > input:focus + label .content),
        .hide-button-outline
          :global(.switch > input:focus:checked + label .content) {
          box-shadow: none !important; /* sorry :/ */
        }
        .hide-button-outline :global(.range-input:focus::-webkit-slider-thumb) {
          box-shadow: none !important;
        }
        .hide-button-outline :global(.range-input:focus::-moz-range-thumb) {
          box-shadow: none !important;
        }
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border-width: 0;
        }
      `}</style>
    </div>
  );
};

export default App;
